import { useParams, Navigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { ArrowLeft, Info } from "lucide-react";
import { useLang } from "@/App";
import { all, pick, seq } from "@/data/images";
import Hero from "@/components/Hero";
import Foto from "@/components/Foto";
import GaleriaGrid from "@/components/GaleriaGrid";
import { Reveal, Pretitulo } from "@/components/Reveal";

const MAPA = { "caminos-del-agua": "caminos", "pinturas-rupestres": "pinturas", "cabalgata-corralito": "cabalgata" };

export default function Actividad() {
  const { slug } = useParams();
  const { t, L } = useLang();
  const key = MAPA[slug];
  const p = key ? t.actividades[key] : null;
  if (!p) return <Navigate to={L("que-hacer")} replace />;
  const g = t.global.footer;

  const galeriaFotos = (() => {
    const hero = pick(p.hero.foto[0], 0) || all(p.hero.foto[0])[0];
    const excl = hero ? [hero.split("/").pop()] : [];
    const propias = seq(p.galeria, 6, excl);
    return propias.filter((f) => f !== hero).slice(0, 6);
  })();

  return (
    <div data-testid={`pagina-actividad-${slug}`}>
      <Hero pre={p.hero.pre} titulo={p.hero.titulo} fotos={[pick(p.hero.foto[0], 0) || all(p.hero.foto[0])[0]]} testid={`hero-actividad-${slug}`} />

      {/* 2. Presentación */}
      <section className="py-16 md:py-24" data-testid={`act-presentacion-${slug}`}>
        <div className="mx-auto max-w-3xl px-6 md:px-10">
          <Link to={L("que-hacer")} className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] text-ochre transition-colors hover:text-terra" data-testid={`act-volver-${slug}`}>
            <ArrowLeft size={14} /> {t.actividades.volver}
          </Link>
          <Reveal className="mt-10 text-center">
            <Pretitulo>{p.pre}</Pretitulo>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink md:text-4xl">{p.titulo}</h2>
            <p className="mt-7 text-left text-[15.5px] leading-relaxed text-ink-soft md:text-center">{p.descripcion}</p>
            {(key === "pinturas" || key === "cabalgata") && (
              <p className="mt-6 inline-flex items-start gap-2 border border-terra/40 bg-terra/5 px-5 py-3 text-left text-[13px] leading-relaxed text-terra" data-testid={`act-aclaracion-${slug}`}>
                <Info size={16} className="mt-0.5 shrink-0" />
                {g.aclaracionPrestador}
              </p>
            )}
          </Reveal>
        </div>
      </section>

      {/* 3. Fotografía principal + ficha */}
      <section className="bg-sand-deep py-20 md:py-28" data-testid={`act-ficha-${slug}`}>
        <div className="mx-auto grid max-w-[1200px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[7fr_5fr] lg:gap-16">
          <Reveal className="img-frame aspect-[4/3] w-full">
            <Foto src={pick(p.foto[0], 0) || all(p.foto[0])[0]} alt="" className="h-full w-full" testid={`act-foto-${slug}`} />
          </Reveal>
          <Reveal delay={0.12}>
            <div className="border border-lineas bg-sand p-7 md:p-9" data-testid={`act-ficha-tabla-${slug}`}>
              <table className="ficha">
                <tbody>
                  {p.ficha.map(([k, v]) => (
                    <tr key={k}>
                      <td>{k}</td>
                      <td>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(key === "pinturas" || key === "cabalgata") && (
                <p className="mt-6 text-[12px] leading-relaxed text-ink-mute">{g.aclaracionPrestador}</p>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4. Galería */}
      <section className="py-20 md:py-28" data-testid={`act-galeria-${slug}`}>
        <div className="mx-auto max-w-[1100px] px-6 md:px-10">
          <TituloGaleria pre={t.actividades.galeria.pre} titulo={t.actividades.galeria.titulo} />
          <GaleriaGrid fotos={galeriaFotos} columnas={3} testid={`act-galeria-grid-${slug}`} />
        </div>
      </section>
    </div>
  );
}

import { TituloSeccion } from "@/components/Reveal";
function TituloGaleria({ pre, titulo }) {
  return <TituloSeccion pre={pre} titulo={titulo} />;
}